import React from 'react';
import { Card, Grid, Typography } from '@mui/material';
import RoamerAndFlexAnnouncementForm from './RoamerAndFlexAnnouncementForm';
import { DateTime } from 'luxon';
import "./EditNotification.scss";
import { appContext } from '../../../../AppContext';
import { IFile } from '../../../../Providers.Api/Files/FilesRepository';
import Helper from '../../../../Common/Helper';
import InspireAnnouncementForm from './InspireAnnouncementForm';
import IbssButton from '../../../../Components/Buttons/Button/IbssButton';
import { RouteComponentProps } from 'react-router';
import LoadingOverlay from '../../../../Components/Navigation/LoadingOverlay/LoadingOverlay';
import { Box } from '@mui/system';
import { IbssPage } from '../../../../Components/Core/BasePage/IbssPage';
import { DateHelper } from '../../../../Common/DateHelper';
import { RichTextEditorComponent } from '@syncfusion/ej2-react-richtexteditor';
import { Announcement, Device } from './DataModels';
import IbssPageHeader from '../../../../Components/Headings/PageHeading/IbssPageHeader';

class EditNotification extends IbssPage<IProps, IState>
{
    private get apiClient() { return appContext().apiClient; }
    private get labels() { return appContext().labels; }
    private roamerFlexRteRef: React.RefObject<RichTextEditorComponent>
    private inspireRteRef: React.RefObject<RichTextEditorComponent>
    private rteValue: string;
    private get localStorage() { return appContext().localStorageProvider; }

    constructor(props: IProps)
    {
        super(props);
        this.roamerFlexRteRef = React.createRef<RichTextEditorComponent>();
        this.inspireRteRef = React.createRef<RichTextEditorComponent>();
        this.rteValue = '';
        this.state =
        {
            notificationTarget: null,
            announcementId: 0,
            buildingId: 0,
            activeDateTime: DateTime.now().set({ minute: 0 }).plus({ hours: 1 }),
            expireDateTime: DateTime.now().set({ minute: 0 }).plus({ hours: 1, days: 1 }),
            customLabelSwitchValue: false,
            titleValue: '',
            shortDescription: '',
            announcementMessage: '',
            images: [],
            headerImageUrl: '',
            customLabel: '',
            highPrioritySwitchValue: false,
            loading: false,
            showFieldValidationError: false,
            titleError: false,
            dateTimeFieldError: false,
            customLabelError: false,
            devices: [],
            selectedDeviceIds: [],
        };
    };

    public async componentDidMount(): Promise<void>
    {
        this.pageTitle = this.labels.funcBuildingAnnouncement_S;
        const buildingId = parseInt(this.props.match.params.buildingid);
        await this.setStateAsync({ buildingId: buildingId });

        if (this.props.match.params.notificationid)
        {            
            const announcement = await appContext().ibssApiClientV2.v2.byNodeid.announcements.byId.get<Announcement>({
                id: parseInt(this.props.match.params.notificationid),
                nodeId: buildingId,
                select: Announcement,
            });

            const announcementData = AnnouncementView.fromAnnouncementToView(announcement);

            await this.setStateAsync({
                notificationTarget: announcementData.target,
                announcementId: announcementData.id,
                activeDateTime: announcementData.availableFrom,
                expireDateTime: announcementData.expiryDate,
                customLabelSwitchValue: announcementData.subclassification != 'info' && announcementData.subclassification != 'INSPIRE',
                titleValue: announcementData.title,
                shortDescription: announcementData.shortMessage,
                announcementMessage: announcementData.message,
                headerImageUrl: announcementData.imageURI,
                customLabel: announcementData.subclassification != 'info' && announcementData.subclassification != 'INSPIRE' ? announcementData.subclassification : '',
                highPrioritySwitchValue: announcementData.priority == 1,
                selectedDeviceIds: this.readNotificationDevice(announcementData.device)
            })
        }
        await this.getImages();
    }

    public async componentDidUpdate(prevProps: IProps, prevState: IState, ): Promise<void> 
    {
        // only get devices for inspire announcements
        if(this.state.notificationTarget !== prevState.notificationTarget && this.state.notificationTarget === NotificationTarget.Inspire)
        {
            await this.getDevices();
        }
    }

    private async getDevices(): Promise<void>
    {
        // returns list of devices available to building.
        try
        {
            this.setState({ loading: true });
            const response = await appContext().ibssApiClientV1.v1.devices.byNodeId.nodedevices.get<Device[]>({
                nodeId: this.state.buildingId,
                recursive: false,
                top: 200,
            });

            const devices = response.map(device => DeviceView.fromDeviceToView(device));
            this.setState({ devices: devices });
        }
        finally
        {
            this.setState({ loading: false });
        }
    }

    private getDeviceOptions(devices: DeviceView[]): IDropdownOption[]
    {
        const options =  devices.map(device => ({ value: device.id, label: device.id }));
        const allOptions = [{ value: 'All', label: this.labels.HubLabelAll }, ...options];
        return allOptions;
    }

    private readNotificationDevice(devices: string[] | null): string[]
    {
        if(devices === null || devices.length === 0)
        {
            return ['All'];
        }
        else
        {
            return devices;
        }
    }

    private get selectedDeviceIds(): string[]
    {
        // in case selected option is 'All', don't pass it along to the api, return an empty array in Notification_Device so the api would target notifications at all devices.
        return this.state.selectedDeviceIds.filter(id => id !== 'All');
    }

    private activeDateTimeChanged(value: DateTime): void
    {
        if(value < this.state.expireDateTime)
        {
            this.setState({ dateTimeFieldError: false });
        }
        this.setState({ activeDateTime: value })
    }

    private expireDateTimeChanged(value: DateTime): void
    {
        if(value > this.state.activeDateTime)
        {
            this.setState({ dateTimeFieldError: false });
        }
        this.setState({ expireDateTime: value })
    }

    private customLabelSwitchChanged(value: boolean): void
    {
        this.setState({ customLabelSwitchValue: value })
    }

    private titleChanged(value: string): void
    {
        if(value != '')
        {
            this.setState({ titleError: false });
        }
        this.setState({ titleValue: value })
    }

    private shortDescriptionChanged(value: string): void
    {
        this.setState({ shortDescription: value })
    }

    private announcementMessageChanged(value: string): void
    {
        this.setState({ announcementMessage: value })
    }

    private headerImageUrlChanged(value: string): void
    {
        this.setState({ headerImageUrl: value })
    }

    private customLabelChanged(value: string): void
    {
        if(value != '')
        {
            this.setState({ customLabelError: false });
        }
        this.setState({ customLabel: value })
    }

    private highPrioritySwitchChanged(value: boolean): void
    {
        this.setState({ highPrioritySwitchValue: value })
    }

    private async getImages(): Promise<void>
    {
        const responseData = await this.apiClient.files.getFiles('c/Notifications');
        const newResponseData = responseData.map((item: IFile) =>
        {
            let newItem = { ...item }
            newItem.path = `https://storage.ibss.${Helper.environment}/images/c/Notifications/${item.name}`
            return newItem
        });

        this.setState({ images: newResponseData });
    }

    private async uploadImage(filename: string, file: FormData, processAsMap: boolean): Promise<void>
    {
        await this.apiClient.files.uploadFile(file, 'c/Notifications/' + filename, processAsMap);
        this.getImages();
    }

    private getFormattedContent(notificationType: string): string
    {
        if(notificationType==='roamerAndFlex')
        {
            this.rteValue = this.roamerFlexRteRef.current?.getHtml() ?? '';
            return this.rteValue;
        }
        else
        {
            this.rteValue = this.inspireRteRef.current?.getHtml() ?? '';
            return this.rteValue;
        }

    }

    private getPayload(draft: boolean): IAnnouncementPayload
    {
        const currentUser = this.localStorage.getUserDetails();
        const { buildingId } = this.state
        let payload: IAnnouncementPayload = 
        {
            ImageURI: '',
            Notification_Title: this.state.titleValue,
            Notification_Priority: 0,
            Classification: 'Building',
            Subclassification: '',
            Notification_Message: '',
            Notification_Short_Message:  this.state.shortDescription,
            Available_From: this.state.activeDateTime.toUtcByNode(buildingId).toISO(),
            Expiry_Date: this.state.expireDateTime.toUtcByNode(buildingId).toISO(),
            Notification_Type: 1,
            Notification_Status: draft ? 'Inactive' : 'Active',
            Notification_Target: this.state.notificationTarget ?? 0,
            Notification_User_Email: currentUser.email,
            Notification_Device: [],
        }

        if (this.state.notificationTarget === NotificationTarget.RoamerAndFlex)
        {
            payload.ImageURI = this.state.headerImageUrl;
            payload.Notification_Priority = this.state.highPrioritySwitchValue ? 1 : 2;
            payload.Subclassification = this.state.customLabelSwitchValue ? this.state.customLabel : this.labels.funcInfo_S;
            payload.Notification_Message = this.getFormattedContent('roamerAndFlex');
            payload.Notification_Device = [];
        }

        if (this.state.notificationTarget === NotificationTarget.Inspire)
        {
            payload.ImageURI = this.state.headerImageUrl;
            payload.Notification_Priority = 2;
            payload.Subclassification = 'INSPIRE';
            payload.Notification_Message = this.getFormattedContent('inspire');
            payload.Notification_Device = this.selectedDeviceIds;
        }

        return payload;
    }

    private async createAnnouncement(draft: boolean): Promise<void>
    {
        this.setState({ loading: true });

        try
        {
            await appContext().ibssApiClientV2.v2.byNodeid.announcements.post
            ({
                nodeId: this.state.buildingId,
                body: this.getPayload(draft),
            });
            this.setState({ loading: false });
            this.props.history.push("/oneLens/" + this.props.match.params.buildingid + "/announcements/list")
        } catch (error)
        {
            this.setState({ loading: false });
        }
    }

    private async editAnnouncement(draft: boolean): Promise<void>
    {
        this.setState({ loading: true });

        try
        {
            await appContext().ibssApiClientV2.v2.byNodeid.announcements.byAnnouncementid.put
            ({
                nodeId: this.state.buildingId,
                announcementid: this.state.announcementId,
                body: this.getPayload(draft),
            });
            this.setState({ loading: false });
            this.props.history.push("/oneLens/" + this.props.match.params.buildingid + "/announcements/list")
        } catch (error)
        {
            this.setState({ loading: false });
        }
    }

    private async publishAnnouncementClicked(draft: boolean): Promise<void>
    {
        this.setState({ showFieldValidationError: true });
        const formValid = this.validateForm();

        if (!formValid)
        {
            return;
        }
        if (this.state.announcementId == 0)
        {
            this.createAnnouncement(draft);
        }
        if (this.state.announcementId != 0)
        {
            this.editAnnouncement(draft);
        }
    }

    private validateForm(): boolean
    {
        let formValid = true;

        const titleValid = this.state.titleValue.length > 0;
        const timesValid = this.state.activeDateTime < this.state.expireDateTime;
        const customLabelValid = this.state.customLabelSwitchValue && this.state.notificationTarget === NotificationTarget.RoamerAndFlex ? this.state.customLabel.length > 0 : true;

        if (!timesValid)
        {
            formValid = false;
            this.setState({ dateTimeFieldError: true });
        }

        if (!titleValid)
        {
            formValid = false;
            this.setState({ titleError: true });
        }

        if (!customLabelValid)
        {
            formValid = false;
            this.setState({ customLabelError: true });
        }

        return formValid;
    }

    public render(): JSX.Element
    {
        return (
            <>
            <Box className="page-height-exct-header">
                {
                    this.state.loading &&
                    <LoadingOverlay />
                }
                <Box className="rightPanel-main-content">
                <IbssPageHeader
                    title={this.state.announcementId == 0 ? this.labels.funcAnnouncementsCreate_L : this.labels.funcAnnouncementsEdit_L}
                />
                <Card>
                    {
                        this.state.announcementId != 0 &&
                        <div className='m-3'> {this.labels.funcAnnouncementID_S}: {this.state.announcementId}</div>
                    }
                    <Box className='m-3'>
                        {
                            this.state.announcementId == 0 &&
                            <Box>
                                <Typography className='mb-3 mt-2 field-heading'>{this.labels.funcAnnouncementBroadcast_Message}</Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                    <Box className={`announcement-type-buttons ${this.state.notificationTarget === NotificationTarget.RoamerAndFlex ? 'announcement-button-selected' : ''}`} onClick={() => this.setState({ notificationTarget: NotificationTarget.RoamerAndFlex })}>
                                        <Typography className='announcement-button-content field-heading'>{this.labels.funcAnnouncementRoamerAndFlex}</Typography>
                                        <Box className='d-flex announcement-button-logo-container'>
                                            <img className='mr-3' width={35} height={35} src={`/images/RoamerLogo.svg`} />
                                            <img width={35} height={35} src={`/images/FlexLogo.svg`} />
                                        </Box>
                                    </Box>
                                    </Grid>
                                    <Grid item xs={6}>
                                    <Box className={`announcement-type-buttons ${this.state.notificationTarget === NotificationTarget.Inspire ? 'announcement-button-selected' : ''}`} onClick={() => this.setState({ notificationTarget: NotificationTarget.Inspire })}>
                                        <Typography className='announcement-button-content field-heading'>{this.labels.HubMenuInspire}</Typography>
                                        <Box className='announcement-button-logo-container'><img width={35} height={35} src={`/images/InspireLogo.svg`} /></Box>
                                    </Box>
                                    </Grid>
                                    </Grid>
                                <hr />
                            </Box>
                        }
                        {
                            this.state.notificationTarget === NotificationTarget.RoamerAndFlex &&
                            <RoamerAndFlexAnnouncementForm
                                activeDateTime={this.state.activeDateTime}
                                onActiveDateTimeChanged={(value: DateTime) => this.activeDateTimeChanged(value)}
                                expireDateTime={this.state.expireDateTime}
                                onExpireDateTimeChanged={(value: DateTime) => this.expireDateTimeChanged(value)}
                                customLabelSwitchValue={this.state.customLabelSwitchValue}
                                editorRef={this.roamerFlexRteRef}
                                onCustomLabelSwitchChanged={(value: boolean) => this.customLabelSwitchChanged(value)}
                                titleValue={this.state.titleValue}
                                onTitleChanged={(value: string) => this.titleChanged(value)}
                                shortDescription={this.state.shortDescription}
                                onShortDescriptionChanged={(value: string) => this.shortDescriptionChanged(value)}
                                announcementMessage={this.state.announcementMessage}
                                images={this.state.images}
                                uploadImage={(filename: string, file: FormData, processAsMap: boolean) => this.uploadImage(filename, file, processAsMap)}
                                headerImageUrl={this.state.headerImageUrl}
                                onHeaderImageUrlChanged={(url: string) => this.headerImageUrlChanged(url)}
                                customLabel={this.state.customLabel}
                                onCustomLabelChanged={(value: string) => this.customLabelChanged(value)}
                                onHighPrioritySwitchChanged={(value: boolean) => this.highPrioritySwitchChanged(value)}
                                highPriority={this.state.highPrioritySwitchValue}
                                showFieldErrors={this.state.showFieldValidationError}
                                titleError={this.state.titleError}
                                dateTimeFieldError={this.state.dateTimeFieldError}
                                customLabelError={this.state.customLabelError}

                            />
                        }
                        {
                            this.state.notificationTarget === NotificationTarget.Inspire &&
                            <InspireAnnouncementForm
                                activeDateTime={this.state.activeDateTime}
                                announcementMessage={this.state.announcementMessage}
                                onActiveDateTimeChanged={(value: DateTime) => this.activeDateTimeChanged(value)}
                                expireDateTime={this.state.expireDateTime}
                                editorRef={this.inspireRteRef}
                                onExpireDateTimeChanged={(value: DateTime) => this.expireDateTimeChanged(value)}
                                titleValue={this.state.titleValue}
                                onTitleChanged={(value: string) => this.titleChanged(value)}
                                scrollingMessage={this.state.shortDescription}
                                onScrollingMessageChanged={(value: string) => this.shortDescriptionChanged(value)}
                                showFieldErrors={this.state.showFieldValidationError}
                                titleError={this.state.titleError}
                                dateTimeFieldError={this.state.dateTimeFieldError}
                                devices={this.getDeviceOptions(this.state.devices)}
                                selectedDeviceIds={this.state.selectedDeviceIds}
                                onDevicesChanged={(deviceIds) => this.setState({ selectedDeviceIds: deviceIds})}
                                headerImageUrl={this.state.headerImageUrl}
                                images={this.state.images}
                                onHeaderImageUrlChanged={(url: string) => this.headerImageUrlChanged(url)}
                                uploadImage={(filename: string, file: FormData, processAsMap: boolean) => this.uploadImage(filename, file, processAsMap)}
                            />
                        }
                    </Box>
                </Card >
                <Box className='d-flex justify-content-between' my={2}>
                    <div>
                        <IbssButton onClick={() => this.props.history.push("/oneLens/" + this.props.match.params.buildingid + "/announcements/list")} variant='contained' color='secondary'>
                            {this.labels.HubButtonCancel}
                        </IbssButton>
                    </div>
                    {
                        this.state.notificationTarget &&
                        <div className='d-flex'>
                            <IbssButton onClick={() => this.publishAnnouncementClicked(true)} className='mr-3' variant='contained' color='secondary'>
                                {this.labels.funcAnnouncementSaveAsDraft_S}
                            </IbssButton>
                            <IbssButton onClick={() => this.publishAnnouncementClicked(false)} variant='contained' color='primary'>
                                {this.labels.funcAnnouncementPublish_S}
                            </IbssButton>
                        </div>
                    }
                </Box>
                </Box>
            </Box>
            </>
        )
    }
}

export default EditNotification;

export interface IProps extends RouteComponentProps<IQueryParams>
{
}

export interface IState
{
    announcementId: number;
    buildingId: number;
    notificationTarget: NotificationTarget | null;
    activeDateTime: DateTime;
    expireDateTime: DateTime;
    customLabelSwitchValue: boolean;
    titleValue: string;
    shortDescription: string;
    announcementMessage: string;
    images: IFile[];
    headerImageUrl: string;
    customLabel: string;
    highPrioritySwitchValue: boolean;
    loading: boolean;
    showFieldValidationError: boolean;
    titleError: boolean,
    dateTimeFieldError: boolean,
    customLabelError: boolean,
    devices: DeviceView[],
    selectedDeviceIds: string[],
}

export interface IQueryParams
{
    buildingid: string;
    notificationid: string;
}

enum NotificationTarget
{
    None = 0,
    RoamerAndFlex = 1,
    Inspire = 2,
    Unity = 4,
    Guest = 8,
}
class AnnouncementView
{
    public id = 0;
    public title = '';
    public priority = 0;
    public recordId = '';
    public message = '';
    public shortMessage = '';
    public reminders =  "";
    public type = 0;
    public status = '';
    public userEmail = '';
    public sent = 0;
    public target = NotificationTarget.None;
    public device = null;
    public classification = '';
    public subclassification = '';
    public availableFrom = DateHelper.null();
    public expiryDate = DateHelper.null();
    public hasImage = 0;
    public imageURI = '';
    public actionURI = '';
    public nodeId = 0;


    public static fromAnnouncementToView(announcement: Announcement): AnnouncementView
    {
        return {
            id: announcement.Notification_Id,
            nodeId: announcement.Node_Id,
            title: announcement.Notification_Title,
            priority: announcement.Notification_Priority,
            recordId: announcement.Notification_Record_Id,
            message: announcement.Notification_Message,
            shortMessage: announcement.Notification_Short_Message,
            reminders: announcement.Notification_Reminders,
            type: announcement.Notification_Type,
            status: announcement.Notification_Status,
            userEmail: announcement.Notification_User_Email,
            sent: announcement.Notification_Sent,
            target: this.getTargetName(announcement.Notification_Target),
            device: announcement.Notification_Device,
            classification: announcement.Classification,
            subclassification: announcement.Subclassification,
            availableFrom: announcement.Available_From,
            expiryDate: announcement.Expiry_Date,
            hasImage: announcement.Has_Image,
            imageURI: announcement.ImageURI,
            actionURI: announcement.ActionURI,
        }
    }

    public static getTargetName(target: number | null): NotificationTarget
    {
        switch(target)
        {
            case 0:
                return NotificationTarget.None;
            case 1:
                return NotificationTarget.RoamerAndFlex;
            case 2:
                return NotificationTarget.Inspire;
            case 4:
                return NotificationTarget.Unity;
            case 8:
                return NotificationTarget.Guest
            default:
                return NotificationTarget.None;
        }
    }
}

export class DeviceView
{
    public id = '';
    public nodeId = 0;
    public spaceId = '';
    public type = 0;
    public code = 0;
    public mode = 0;
    public signageId = '';
    public authError = '';
    public generalError = '';
    public OEMMode = 0;
    public OEMClientImageURI = '';
    public OEMBrandImageURI = '';
    public MAC = '';
    public IP = '';

    public static fromDeviceToView(device: Device ): DeviceView
    {
        return ({
            id: device.Device_Id.toString(),
            nodeId: device.Node_Id,
            spaceId: device.Space_Id,
            type: device.Device_Type,
            code: device.Device_Code,
            mode: device.Device_Mode,
            signageId: device.Signage_Id.toString(),
            authError: device.Device_Auth_Error,
            generalError: device.Device_General_Error,
            OEMMode: device.Device_OEM_Mode,
            OEMClientImageURI: device.Device_OEM_Client_ImageURI,
            OEMBrandImageURI: device.Device_OEM_Brand_ImageURI,
            MAC: device.Device_MAC,
            IP: device.Device_IP,
        });
    }
}

export interface IDropdownOption
{
    label: string;
    value: string;
}

interface IAnnouncementPayload
{
    ImageURI: string;
    Notification_Title: string;
    Notification_Priority: number;
    Classification: string;
    Subclassification: string;
    Notification_Message: string;
    Notification_Short_Message: string;
    Available_From: string;
    Expiry_Date: string;
    Notification_Type: number;
    Notification_Status: string;
    Notification_Target: number,
    Notification_User_Email: string;
    Notification_Device: string[];
}